import { FC, FunctionComponent, useContext, useEffect, useRef, useState} from "react";
import DashboardSideBar from "../../components/shared-components/dashboard/DashboardSideBar";
import DashboardHeader from "../../components/shared-components/dashboard/DashboardHeader";
import styles from "./DashboardLayout.module.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_USER_PROFILE, UPDATE_USER_DEVICE_TOKEN } from "../../features/Auth/api/SignInQuery";
import ToasterWithMessage from '../../components/shared-components/toaster/index'
import { globalContext } from "../../Provider/AppProvider";
import { firebase } from "../../config/firebaseconfig";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { register } from "../../serviceWorkerRegistration";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { GET_NOTIFICATIONS } from "../../components/shared-components/notification/api/NotificationQuery";
import { getFormattedDate, roles, resolvePreferredPortalRole } from "../../helper";
import { getNotificationQueryVariables, mergeNotificationQueryIntoGlobalState } from "../../utils/notification-query-variables";
import { CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";

interface ProType {
  component: FunctionComponent;
}

const DashboardLayout: FC<ProType> = ({ component: Component }) => {
  const {error} = ToasterWithMessage();
  const [loading,setLoading] = useState(false);
  const {globalState,handleGlobalState} = useContext<any>(globalContext)
  const globalStateRef = useRef(globalState);
  const [deviceToken,setDeviceToken] = useState("");
  const [existingDeviceToken,setExistingDeviceToken] = useState<string[]>([]);
  const[userId,setUserId] = useState("");
  
  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const date = getFormattedDate(new Date());
  const {pathname} = useLocation();

  // Check if mobile view
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  }, [pathname, isMobile]);

  // Sidebar should always be open on desktop, controlled by state on mobile
  const showSidebar = !isMobile ? true : isMobileSidebarOpen;

  const handleMenuClick = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const [updateUserDeviceToken] = useMutation(UPDATE_USER_DEVICE_TOKEN)
  
  const [getUserProfile] = useLazyQuery(GET_USER_PROFILE, {
    fetchPolicy: "no-cache",
    onCompleted: (userdata) => {
      const adminFName = userdata?.get_user_profile[0]?.u_first_name;
      const adminLName = userdata?.get_user_profile[0]?.u_last_name;
      const mailId = userdata?.get_user_profile[0]?.u_email_id;
      const avatar = userdata?.get_user_profile[0]?.u_avatar_url;
      const phoneNumber = userdata?.get_user_profile[0]?.u_phone_number;
      const userId = userdata?.get_user_profile[0]?.u_id;
      const userRole=userdata?.get_user_profile[0]?.u_role;
      const allowedRoles = userdata?.get_user_profile?.[0]?.u_allowed_roles || [];
      const storedRole = localStorage.getItem("x-hasura-user-role") || "";
      const preferredRole = resolvePreferredPortalRole(allowedRoles, userRole);
      const tempData = {...globalState}
      const resolvedRole =
        (storedRole && allowedRoles.includes(storedRole) && storedRole) ||
        preferredRole ||
        userRole ||
        allowedRoles?.[0] ||
        tempData?.profile?.userRole;
      tempData.profile.id = userId? userId:"";
      tempData.profile.firstName = adminFName ? adminFName : tempData?.profile?.firstName
      tempData.profile.lastName = (adminLName && adminLName.trim() !== '')? adminLName: adminLName === ''? '': tempData?.profile?.lastName ;
      tempData.profile.mailId = mailId ? mailId : tempData?.profile?.mailId
      tempData.profile.photo = avatar ? avatar : tempData?.profile?.photo
      tempData.profile.phonenumber = phoneNumber ? phoneNumber : tempData?.profile?.phonenumber
      tempData.profile.profileUpdate = false;
      tempData.profile.userRole = resolvedRole;
      if (resolvedRole) {
        localStorage.setItem("x-hasura-user-role", resolvedRole);
      }
      handleGlobalState({...tempData,tempData});
      setLoading(false);
      setUserId(userId);
      setExistingDeviceToken(userdata?.get_user_profile[0]?.u_device_tokens)
    },
    onError: ()=>{
        setLoading(false)
        error("Something went wrong!");
    }
  });

  
  const notificationBaselineUserRef = useRef<string | null>(null);

  const [getWebNotification] = useLazyQuery(GET_NOTIFICATIONS,{
    fetchPolicy:"no-cache",
    onCompleted:(data)=>{
      const activeUserId = globalStateRef.current?.profile?.id;
      handleGlobalState((prev: typeof globalState) =>
        mergeNotificationQueryIntoGlobalState(prev, data, {
          baselineOnly: notificationBaselineUserRef.current !== activeUserId,
        }),
      );
      if (activeUserId) {
        notificationBaselineUserRef.current = activeUserId;
      }
    }
  })

  useEffect(()=>{
    if(globalState?.profile?.profileUpdate){
      setLoading(true);
      getUserProfile();
    }
  }, [globalState?.profile?.profileUpdate])
  
  useEffect(() => {
    globalStateRef.current = globalState;
  }, [globalState]);

  useEffect(()=>{
    setLoading(true);
    getUserProfile();
    getDeviceToken();
  }, []);

  useEffect(() => {
    if (!userId) return;
    getWebNotification({
      variables: getNotificationQueryVariables(userId),
    });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const intervalId = window.setInterval(() => {
      getWebNotification({
        variables: getNotificationQueryVariables(userId),
      });
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [userId]);

  const getDeviceToken = async () => {
    try {
      const messagingSupported = await isSupported().catch(() => false);
      if (!messagingSupported || typeof window === "undefined" || !("Notification" in window)) {
        return;
      }

      const messaging = getMessaging(firebase);

      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        const serviceWorkerRegistration = await register();
        const currentToken = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_VAPID_KEY,
          serviceWorkerRegistration: serviceWorkerRegistration,
        });
  
        if (currentToken) {
          const tempData = {...globalState};
          tempData.profile.deviceToken = currentToken;
          handleGlobalState(tempData);
          setDeviceToken(currentToken);

          onMessage(messaging, (payload) => {
            const {data} = payload;
            const title = data?.title || "Message"
          
            Notification.requestPermission().then((permission) => {
              if (permission === 'granted'){
                const notification = new Notification(title, {
                  icon: data?.imageUrl,
                });
              }
            }).catch((err: any) => {
            });

            const latestState = globalStateRef.current;
            if(latestState?.profile?.id){
              getWebNotification({
                variables: getNotificationQueryVariables(latestState.profile.id),
              })
            }
          });
        }
      }
    } catch (err: any) {
      // error("Something went wrong while get device token!");
    }
  };

  useEffect(()=>{
    if(deviceToken && !loading && userId){
      const shouldUpdate =
        !existingDeviceToken || !existingDeviceToken.includes(deviceToken);
      if (!shouldUpdate) return;

      updateUserDeviceToken({
        variables:{
          userId: userId,
          deviceToken: deviceToken
        },
      }).catch(() => {
        // Non-blocking: push notification token sync must not break dashboard pages.
      });
    }
  }, [deviceToken, loading, userId])

  return (
    <>
      <div className={styles.adminDashboard}>
        {/* Sidebar with mobile overlay support */}
        <DashboardSideBar showSidebar={showSidebar} />
        
        {/* Mobile sidebar overlay */}
        {isMobile && isMobileSidebarOpen && (
          <div 
            className={styles.sidebarOverlay} 
            onClick={handleMenuClick}
          />
        )}
        
        <div className={`${styles.dashboardLayoutMainStyle} ${isMobile && isMobileSidebarOpen ? styles.blurBackground : ''}`}>
          <DashboardHeader 
            loadingUserProfile={loading} 
            onMenuClick={handleMenuClick}
            isSidebarOpen={isMobileSidebarOpen}
          />
          <div className={styles.mainContent} style={{height: `${(pathname === "/new-list" || (globalState.profile.userRole === roles.SUPER_USER && pathname === "/dashboard")) ? "100vh" : ""}`}}>

            {loading ?
              <div className={styles.TableCircularProgress}>
                <CircularProgress color="primary" size={35} />
              </div> :
              <Component />
            } 
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardLayout;